import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { CUSTOMERS } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search, X, ChevronUp, ChevronDown, ChevronsUpDown, Download, Upload, RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ColumnFilter from '@/components/ColumnFilter';
import CsvImportDialog from '@/components/CsvImportDialog';
import { downloadPdf } from '@/lib/api';

const empty = { nro: '', suonentieto_no: '', ryhma: 'HOK', ketju: 'Prisma', asiakas: '', reitti: 'KP', active: true, aliases: [], price_group: '1', show_price: true, ytunnus: '', billing_name: '', lasku_nimi: '', billing_street: '', billing_postal_code: '', billing_city: '', billing_country: 'FI', einvoice_address: '', einvoice_operator_id: '', delivery_note: '', on_kylmio: false };

const PRICE_GROUP_LABEL_FALLBACK = { '1': '1 Kaupat', '2': '2 Tukku', '3': '3 Muu' };

// OVT-tunnus is always "0037" + 8-digit Y-tunnus (no hyphen).
// Returns '' if Y-tunnus is missing or invalid length.
function ytunnusToOvt(ytunnus) {
  const digits = String(ytunnus || '').replace(/\D/g, '');
  return digits.length === 8 ? `0037${digits}` : '';
}

// Constant välittäjätunnus per ryhmä (chain operator), näyttömuodossa
// "Nimi (Tunnus)" — vastaa Suonentiedon näyttötyyliä. Tallennetaan
// einvoice_operator_id-kenttään sellaisenaan; Finvoice-XML poimii ID:n
// suluista.
const EINVOICE_OPERATOR_BY_RYHMA = {
  'K-Ryhmä': 'OpusCapita Solutions Oy (E204503)',
  'HOK': 'TietoEvry Oyj (003701011385)',
};
function operatorForRyhma(ryhma) {
  return EINVOICE_OPERATOR_BY_RYHMA[(ryhma || '').trim()] || '';
}

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [autofillBusy, setAutofillBusy] = useState(false);
  const [onlyMissingEinvoice, setOnlyMissingEinvoice] = useState(false);
  // Which missing field to focus on when the filter is active.
  // 'any' = customer is missing at least one of Y/OVT/operator (default).
  // 'y'   = missing Y-tunnus
  // 'ovt' = missing OVT-tunnus (einvoice_address)
  // 'op'  = missing välittäjätunnus (einvoice_operator_id)
  const [missingFocus, setMissingFocus] = useState('any');
  const [searchParams, setSearchParams] = useSearchParams();
  const priceGroupFilter = searchParams.get('price_group') || '';
  const [items, setItems] = useState([]);
  // Hintaryhmät ladataan dynaamisesti `/api/price-groups`-päätepisteestä,
  // jotta käyttäjän lisäämät custom-ryhmät ("4 Erikoiserä", "premium", …)
  // näkyvät dropdownissa ja chip-labeleissa automaattisesti.
  const [priceGroups, setPriceGroups] = useState([]);
  const priceGroupLabels = useMemo(() => {
    const m = { ...PRICE_GROUP_LABEL_FALLBACK };
    for (const g of priceGroups) m[g.key] = `${g.key} ${g.name}`;
    return m;
  }, [priceGroups]);
  const [reference, setReference] = useState({ ryhma: [], ketju: [], reitti: [] });
  const [importOpen, setImportOpen] = useState(false);
  // Duplicate detection: shown as a small banner with a click-through modal.
  const [dupGroups, setDupGroups] = useState([]);
  const [dupModalOpen, setDupModalOpen] = useState(false);
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem('customers.filter') || ''; } catch { return ''; }
  });
  useEffect(() => {
    try { localStorage.setItem('customers.filter', filter); } catch {
      // ignore
    }
  }, [filter]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, else customer object
  const [form, setForm] = useState(empty);
  const [sort, setSort] = useState(() => {
    try {
      const raw = localStorage.getItem('customers.sort');
      if (raw) {
        const v = JSON.parse(raw);
        if (v && v.key && v.dir) return v;
      }
    } catch {
      // ignore parse errors — fallback to default
    }
    return { key: 'asiakas', dir: 'asc' };
  });

  useEffect(() => {
    try {
      localStorage.setItem('customers.sort', JSON.stringify(sort));
    } catch {
      // ignore storage errors
    }
  }, [sort]);

  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return { key: 'asiakas', dir: 'asc' }; // 3rd click → default
    });
  };

  // Per-column filters (multi-select). Persisted to localStorage.
  const FILTER_KEYS = ['ryhma', 'ketju', 'reitti', 'price_group', 'active'];
  const [colFilters, setColFilters] = useState(() => {
    try {
      const raw = localStorage.getItem('customers.colFilters');
      if (raw) {
        const v = JSON.parse(raw);
        if (v && typeof v === 'object') {
          const out = {};
          for (const k of FILTER_KEYS) out[k] = Array.isArray(v[k]) ? v[k] : [];
          return out;
        }
      }
    } catch {
      // ignore
    }
    return { ryhma: [], ketju: [], reitti: [], price_group: [], active: [] };
  });

  useEffect(() => {
    try {
      localStorage.setItem('customers.colFilters', JSON.stringify(colFilters));
    } catch {
      // ignore
    }
  }, [colFilters]);

  const updateColFilter = (key) => (next) => {
    setColFilters((c) => ({ ...c, [key]: next }));
  };

  const activeFilterCount = Object.values(colFilters).reduce((a, arr) => a + (arr?.length || 0), 0);

  // Option lists derived from current items so users only see real values.
  const optionLists = useMemo(() => {
    const ryhma = new Set();
    const ketju = new Set();
    const reitti = new Set();
    const price_group = new Set();
    for (const c of items) {
      if (c.ryhma) ryhma.add(c.ryhma);
      if (c.ketju) ketju.add(c.ketju);
      if (c.reitti) reitti.add(c.reitti);
      if (c.price_group) price_group.add(String(c.price_group));
    }
    return {
      ryhma: Array.from(ryhma),
      ketju: Array.from(ketju),
      reitti: Array.from(reitti),
      price_group: Array.from(price_group),
      active: ['Käytössä', 'Pois käytöstä'],
    };
  }, [items]);

  const load = async () => {
    try {
      const [{ data: list }, { data: ref }, { data: pg }] = await Promise.all([
        api.get('/customers'),
        api.get('/reference'),
        api.get('/price-groups'),
      ]);
      setItems(list);
      setReference(ref);
      setPriceGroups(pg || []);
      // Refresh duplicate count in background
      try {
        const { data } = await api.get('/customers/duplicates');
        setDupGroups(data.groups || []);
      } catch {
        // ignore — endpoint may not yet be deployed
      }
    } catch (e) {
      toast.error('Lataus epäonnistui');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...c, aliases: c.aliases || [] });
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, aliases: typeof form.aliases === 'string' ? form.aliases.split(',').map((s) => s.trim()).filter(Boolean) : form.aliases };
      if (editing) {
        await api.patch(`/customers/${editing.id}`, payload);
        toast.success('Tallennettu');
      } else {
        await api.post('/customers', payload);
        toast.success('Lisätty');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Tallennus epäonnistui');
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/customers/${c.id}`, { active: !c.active });
      load();
    } catch {
      toast.error('Tila ei päivittynyt');
    }
  };

  // Inline route edit straight from the list — saves a click vs. opening the dialog.
  const updateRoute = async (c, newRoute) => {
    if (!newRoute || newRoute === c.reitti) return;
    try {
      await api.patch(`/customers/${c.id}`, { reitti: newRoute });
      // Optimistic local update so the UI updates without re-fetching everything
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, reitti: newRoute } : x)));
      toast.success(`${c.asiakas} → reitti ${newRoute}`);
    } catch {
      toast.error('Reitin päivitys epäonnistui');
    }
  };

  // Inline hintaryhmä edit — common change when reclassifying a customer between kaupat/tukku/muu.
  const updatePriceGroup = async (c, newGroup) => {
    const v = String(newGroup || '');
    if (!v || v === String(c.price_group || '')) return;
    try {
      await api.patch(`/customers/${c.id}`, { price_group: v });
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, price_group: v } : x)));
      toast.success(`${c.asiakas} → hintaryhmä ${priceGroupLabels[v] || v}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hintaryhmän päivitys epäonnistui');
    }
  };

  // Inline asiakasnumero edit — committed on blur/Enter; cancelled on Esc.
  const updateNro = async (c, newNro) => {
    const v = (newNro || '').trim();
    if (v === (c.nro || '').trim()) return;
    try {
      await api.patch(`/customers/${c.id}`, { nro: v });
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, nro: v } : x)));
      toast.success(`${c.asiakas} → nro ${v || '—'}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Asiakasnumeron päivitys epäonnistui');
    }
  };

  // Admin action: backfill OVT-tunnus + välittäjätunnus for any customer
  // missing them, derived from Y-tunnus + ryhmä. Idempotent.
  const runFinvoiceAutofill = async () => {
    if (autofillBusy) return;
    setAutofillBusy(true);
    try {
      const r = await api.post('/customers/autofill-einvoice');
      const n = r.data?.updated || 0;
      const by = r.data?.by_ryhma || {};
      const breakdown = Object.entries(by).map(([k, v]) => `${k}: ${v}`).join(', ');
      if (n === 0) {
        toast.success('Kaikki asiakkaat ovat jo täytetty — ei muutoksia.');
      } else {
        toast.success(`Päivitettiin ${n} asiakkaan Finvoice-reititys${breakdown ? ` (${breakdown})` : ''}`);
      }
      // Refresh the list so the updated values appear in the table
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Päivitys epäonnistui');
    } finally {
      setAutofillBusy(false);
    }
  };

  const [postalCleanBusy, setPostalCleanBusy] = useState(false);
  const cleanPostalCodes = async () => {
    if (!confirm('Lisätään puuttuvat etunollat asiakkaiden postinumeroihin (esim. 900 → 00900). Olemassa olevat 5-merkkiset koodit jätetään ennalleen. Jatketaanko?')) return;
    setPostalCleanBusy(true);
    try {
      const r = await api.post('/customers/admin/normalize-postal-codes');
      const n = r.data?.fixed_count || 0;
      const sample = (r.data?.fixed || []).slice(0, 5)
        .map((f) => `${f.asiakas}: ${f.before} → ${f.after}`)
        .join(' · ');
      if (n === 0) {
        toast.success('Kaikki postinumerot ovat jo oikein.');
      } else {
        toast.success(`Korjattu ${n} postinumeroa${sample ? `. Esim. ${sample}${n > 5 ? '...' : ''}` : ''}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Postinumerojen siivous epäonnistui');
    } finally {
      setPostalCleanBusy(false);
    }
  };

  const remove = async (c) => {
    if (!confirm(`Poistetaanko asiakas ${c.asiakas}? Kaikki sen tilaukset poistetaan.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      load();
    } catch {
      toast.error('Poisto epäonnistui');
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (priceGroupFilter) {
      list = list.filter((c) => c.price_group === priceGroupFilter);
    }
    // Per-column multi-select filters
    if (colFilters.ryhma.length > 0) {
      const s = new Set(colFilters.ryhma);
      list = list.filter((c) => s.has(c.ryhma));
    }
    if (colFilters.ketju.length > 0) {
      const s = new Set(colFilters.ketju);
      list = list.filter((c) => s.has(c.ketju));
    }
    if (colFilters.reitti.length > 0) {
      const s = new Set(colFilters.reitti);
      list = list.filter((c) => s.has(c.reitti));
    }
    if (colFilters.price_group.length > 0) {
      const s = new Set(colFilters.price_group.map(String));
      list = list.filter((c) => s.has(String(c.price_group)));
    }
    if (colFilters.active.length > 0) {
      const wantActive = colFilters.active.includes('Käytössä');
      const wantInactive = colFilters.active.includes('Pois käytöstä');
      if (wantActive && !wantInactive) list = list.filter((c) => !!c.active);
      else if (wantInactive && !wantActive) list = list.filter((c) => !c.active);
    }
    if (filter.trim()) {
      const q = filter.toLowerCase();
      list = list.filter((c) =>
        [c.nro, c.ryhma, c.ketju, c.asiakas, c.reitti].some((v) => (v || '').toLowerCase().includes(q))
      );
    }
    if (onlyMissingEinvoice) {
      // Highlight customers ready-for-Finvoice cleanup: missing Y, OVT, operator
      // or — for S-Ryhmä (HOK) shops sharing one Y-tunnus — missing lasku_nimi
      // which is what Suonentieto uses to disambiguate the right shop.
      const isBlank = (v) => !((v || '').toString().trim());
      const isHok = (c) => ((c.ryhma || '').trim().toUpperCase() === 'HOK');
      if (missingFocus === 'y') {
        list = list.filter((c) => isBlank(c.ytunnus));
      } else if (missingFocus === 'ovt') {
        list = list.filter((c) => isBlank(c.einvoice_address));
      } else if (missingFocus === 'op') {
        list = list.filter((c) => isBlank(c.einvoice_operator_id));
      } else if (missingFocus === 'ln') {
        list = list.filter((c) => isHok(c) && isBlank(c.lasku_nimi));
      } else {
        list = list.filter((c) =>
          isBlank(c.ytunnus) || isBlank(c.einvoice_address) || isBlank(c.einvoice_operator_id) ||
          (isHok(c) && isBlank(c.lasku_nimi))
        );
      }
    }
    // Sort (immutable copy)
    const { key, dir } = sort;
    const factor = dir === 'desc' ? -1 : 1;
    const collator = new Intl.Collator('fi', { sensitivity: 'base', numeric: true });
    return [...list].sort((a, b) => {
      const av = (a[key] ?? '').toString();
      const bv = (b[key] ?? '').toString();
      const cmp = collator.compare(av, bv);
      if (cmp !== 0) return cmp * factor;
      // Stable tiebreaker by asiakas
      return collator.compare(a.asiakas || '', b.asiakas || '');
    });
  }, [items, filter, priceGroupFilter, sort, colFilters, onlyMissingEinvoice, missingFocus]);

  // Count of customers missing Finvoice essentials — shown next to the
  // filter toggle so admin sees instantly how many records need fixing.
  const einvoiceBreakdown = useMemo(() => {
    const isBlank = (v) => !((v || '').toString().trim());
    const isHok = (c) => ((c.ryhma || '').trim().toUpperCase() === 'HOK');
    let missingAny = 0;
    let missingY = 0;
    let missingOvt = 0;
    let missingOp = 0;
    let missingLaskuNimi = 0;
    for (const c of items) {
      const noY = isBlank(c.ytunnus);
      const noO = isBlank(c.einvoice_address);
      const noP = isBlank(c.einvoice_operator_id);
      const noLn = isHok(c) && isBlank(c.lasku_nimi);
      if (noY || noO || noP || noLn) missingAny += 1;
      if (noY) missingY += 1;
      if (noO) missingOvt += 1;
      if (noP) missingOp += 1;
      if (noLn) missingLaskuNimi += 1;
    }
    return { missingAny, missingY, missingOvt, missingOp, missingLaskuNimi };
  }, [items]);
  const missingEinvoiceCount = einvoiceBreakdown.missingAny;

  const clearPriceGroup = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('price_group');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-5">
      {dupGroups.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#FBEAE5] border border-[#C05A3C]/30 text-[#7A3520] rounded-xl px-4 py-2.5">
          <div className="flex items-start sm:items-center gap-2.5 text-sm">
            <AlertTriangle size={16} className="text-[#C05A3C] shrink-0 mt-0.5 sm:mt-0" />
            <span>
              Löytyi <strong>{dupGroups.length}</strong> mahdollista duplikaattiasiakasta. Suonentieto saattaa luoda asiakkaan kahteen kertaan jos ei korjata.
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDupModalOpen(true)}
            data-testid="customers-show-duplicates-button"
            className="h-9 rounded-full border-[#C05A3C] text-[#C05A3C] hover:bg-[#F5D6CD] w-full sm:w-auto shrink-0"
          >
            Näytä &amp; siivoa
          </Button>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">Hallinta</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">Asiakkaat</h1>
          <p className="text-sm text-[#6A6D66] mt-1">Aktiiviset asiakkaat näkyvät automaattisesti jokaisella päivällä.</p>
          {priceGroupFilter && (
            <button
              type="button"
              onClick={clearPriceGroup}
              data-testid="customers-clear-pricegroup-filter"
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#2A4B30] text-white hover:bg-[#1E3622] transition-colors"
              title="Poista hintaryhmäsuodatin"
            >
              Hintaryhmä: {priceGroupLabels[priceGroupFilter] || priceGroupFilter}
              <X size={12} />
            </button>
          )}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => setColFilters({ ryhma: [], ketju: [], reitti: [], price_group: [], active: [] })}
              data-testid="customers-clear-all-filters"
              className="ml-2 mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#FBEAE5] text-[#C05A3C] hover:bg-[#F5D6CD] transition-colors"
              title="Tyhjennä kaikki sarakesuodattimet"
            >
              {activeFilterCount} suodatin{activeFilterCount === 1 ? '' : 'ta'} käytössä
              <X size={12} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none min-w-[180px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A6D66]" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Hae…"
              className="pl-8 h-10 w-full sm:w-56"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="customers-import-csv-button"
            className="h-9 rounded-full border-[#E5E6E3] hover:bg-[#F4F6F3]"
          >
            <Upload size={15} className="mr-1.5" />
            Tuo CSV
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              try {
                const fname = await downloadPdf('/api/customers/export.csv', 'asiakkaat.csv');
                toast.success(`Ladattu: ${fname}`);
              } catch (e) {
                toast.error(`CSV-vienti epäonnistui: ${e.message || e}`);
              }
            }}
            data-testid="customers-export-csv-button"
            className="h-9 rounded-full border-[#E5E6E3] hover:bg-[#F4F6F3]"
          >
            <Download size={15} className="mr-1.5" />
            Vie CSV
          </Button>
          {isAdmin && (
            <Button
              type="button"
              variant={onlyMissingEinvoice ? 'default' : 'outline'}
              onClick={() => {
                setOnlyMissingEinvoice((v) => !v);
                setMissingFocus('any');
              }}
              data-testid="customers-filter-missing-einvoice-button"
              title="Näytä vain asiakkaat joilta puuttuu Y-tunnus, OVT-tunnus tai välittäjätunnus"
              className={`h-9 rounded-full transition-colors ${
                onlyMissingEinvoice
                  ? 'bg-[#C05A3C] hover:bg-[#A14A30] text-white'
                  : 'border-[#E5E6E3] hover:bg-[#F4F6F3]'
              }`}
            >
              <AlertCircle size={15} className="mr-1.5" />
              Puuttuvat tunnukset
              <span
                className={`ml-1.5 font-mono text-[10px] px-1.5 py-0.5 rounded-full ${
                  onlyMissingEinvoice ? 'bg-white/20' : 'bg-[#E5E6E3] text-[#6A6D66]'
                }`}
              >
                {missingEinvoiceCount}
              </span>
            </Button>
          )}
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              onClick={cleanPostalCodes}
              disabled={postalCleanBusy}
              data-testid="customers-clean-postal-codes-button"
              title="Lisää puuttuvat etunollat suomalaisiin postinumeroihin (esim. 900 → 00900). Idempotentti — turvallinen ajaa monta kertaa."
              className="h-9 rounded-full border-[#E5E6E3] hover:bg-[#F4F6F3]"
            >
              <RefreshCw size={15} className={`mr-1.5 ${postalCleanBusy ? 'animate-spin' : ''}`} />
              {postalCleanBusy ? 'Siivotaan…' : 'Siivoa postinumerot'}
            </Button>
          )}
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              onClick={runFinvoiceAutofill}
              disabled={autofillBusy}
              data-testid="customers-autofill-einvoice-button"
              title="Täytä OVT-tunnukset ja välittäjätunnukset Y-tunnuksen ja ryhmän perusteella kaikille asiakkaille (idempotentti)"
              className="h-9 rounded-full border-[#E5E6E3] hover:bg-[#F4F6F3]"
            >
              <RefreshCw size={15} className={`mr-1.5 ${autofillBusy ? 'animate-spin' : ''}`} />
              {autofillBusy ? 'Päivitetään…' : 'Päivitä Finvoice-reititys'}
            </Button>
          )}
          <Button
            onClick={openNew}
            data-testid={CUSTOMERS.addButton}
            className="h-9 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white"
          >
            <Plus size={16} className="mr-1" />
            Lisää asiakas
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#E5E6E3] bg-white shadow-sm">
        {onlyMissingEinvoice && isAdmin && (
          <div
            data-testid="customers-missing-breakdown"
            className="flex items-center gap-2 flex-wrap px-4 py-2 bg-[#FDF6EB] border-b border-[#E9D9B5] text-xs"
          >
            <span className="text-[#6A6D66] mr-1">Puuttuvat kentät:</span>
            <button
              type="button"
              data-testid="customers-missing-focus-any"
              onClick={() => setMissingFocus('any')}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                missingFocus === 'any'
                  ? 'bg-[#C05A3C] border-[#C05A3C] text-white'
                  : 'bg-white border-[#E9D9B5] hover:bg-[#F4F6F3]'
              }`}
            >
              <span className={missingFocus === 'any' ? 'text-white/90' : 'text-[#6A6D66]'}>Kaikki</span>
              <span className={`font-semibold tabular-nums ${missingFocus === 'any' ? 'text-white' : 'text-[#C05A3C]'}`}>{einvoiceBreakdown.missingAny}</span>
            </button>
            <button
              type="button"
              data-testid="customers-missing-focus-y"
              onClick={() => setMissingFocus('y')}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                missingFocus === 'y'
                  ? 'bg-[#C05A3C] border-[#C05A3C] text-white'
                  : 'bg-white border-[#E9D9B5] hover:bg-[#F4F6F3]'
              }`}
            >
              <span className={missingFocus === 'y' ? 'text-white/90' : 'text-[#6A6D66]'}>Y-tunnus</span>
              <span className={`font-semibold tabular-nums ${missingFocus === 'y' ? 'text-white' : 'text-[#C05A3C]'}`}>{einvoiceBreakdown.missingY}</span>
            </button>
            <button
              type="button"
              data-testid="customers-missing-focus-ovt"
              onClick={() => setMissingFocus('ovt')}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                missingFocus === 'ovt'
                  ? 'bg-[#C05A3C] border-[#C05A3C] text-white'
                  : 'bg-white border-[#E9D9B5] hover:bg-[#F4F6F3]'
              }`}
            >
              <span className={missingFocus === 'ovt' ? 'text-white/90' : 'text-[#6A6D66]'}>OVT-tunnus</span>
              <span className={`font-semibold tabular-nums ${missingFocus === 'ovt' ? 'text-white' : 'text-[#C05A3C]'}`}>{einvoiceBreakdown.missingOvt}</span>
            </button>
            <button
              type="button"
              data-testid="customers-missing-focus-op"
              onClick={() => setMissingFocus('op')}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                missingFocus === 'op'
                  ? 'bg-[#C05A3C] border-[#C05A3C] text-white'
                  : 'bg-white border-[#E9D9B5] hover:bg-[#F4F6F3]'
              }`}
            >
              <span className={missingFocus === 'op' ? 'text-white/90' : 'text-[#6A6D66]'}>Välittäjätunnus</span>
              <span className={`font-semibold tabular-nums ${missingFocus === 'op' ? 'text-white' : 'text-[#C05A3C]'}`}>{einvoiceBreakdown.missingOp}</span>
            </button>
            <button
              type="button"
              data-testid="customers-missing-focus-lasku-nimi"
              onClick={() => setMissingFocus('ln')}
              title="Näytä S-Ryhmän (HOK) kaupat joilta puuttuu lasku_nimi — kentällä Suonentieto erottaa jaetun Y-tunnuksen kauppojen joukosta oikean asiakkaan"
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                missingFocus === 'ln'
                  ? 'bg-[#C05A3C] border-[#C05A3C] text-white'
                  : 'bg-white border-[#E9D9B5] hover:bg-[#F4F6F3]'
              }`}
            >
              <span className={missingFocus === 'ln' ? 'text-white/90' : 'text-[#6A6D66]'}>HOK lasku_nimi</span>
              <span className={`font-semibold tabular-nums ${missingFocus === 'ln' ? 'text-white' : 'text-[#C05A3C]'}`}>{einvoiceBreakdown.missingLaskuNimi}</span>
            </button>
            <span className="text-[10px] text-[#6A6D66] ml-2">
              Vinkki: aja ensin &quot;Päivitä Finvoice-reititys&quot; — täyttää Y-tunnuksesta ja ryhmästä mitä voidaan.
            </span>
          </div>
        )}
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F9F9F6] border-b border-[#E5E6E3] text-[#6A6D66]">
            <tr>
              <SortableTh field="nro" sort={sort} onClick={toggleSort} className="w-24">Nro</SortableTh>
              <SortableTh field="ryhma" sort={sort} onClick={toggleSort} className="w-28">Ryhmä</SortableTh>
              <SortableTh field="ketju" sort={sort} onClick={toggleSort} className="w-32">Ketju</SortableTh>
              <SortableTh field="asiakas" sort={sort} onClick={toggleSort}>Asiakas</SortableTh>
              <SortableTh field="reitti" sort={sort} onClick={toggleSort} className="w-28">Reitti</SortableTh>
              <SortableTh field="price_group" sort={sort} onClick={toggleSort} className="w-28">Hintaryhmä</SortableTh>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-24">Hinta läh.</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Aktiivinen</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32"></th>
            </tr>
            <tr className="border-t border-[#E5E6E3] bg-white">
              <th className="px-3 py-1.5"></th>
              <th className="px-3 py-1.5">
                <ColumnFilter
                  label="Ryhmä"
                  options={optionLists.ryhma}
                  selected={colFilters.ryhma}
                  onChange={updateColFilter('ryhma')}
                  testId="customers-filter-ryhma"
                />
              </th>
              <th className="px-3 py-1.5">
                <ColumnFilter
                  label="Ketju"
                  options={optionLists.ketju}
                  selected={colFilters.ketju}
                  onChange={updateColFilter('ketju')}
                  testId="customers-filter-ketju"
                />
              </th>
              <th className="px-3 py-1.5"></th>
              <th className="px-3 py-1.5">
                <ColumnFilter
                  label="Reitti"
                  options={optionLists.reitti}
                  selected={colFilters.reitti}
                  onChange={updateColFilter('reitti')}
                  testId="customers-filter-reitti"
                />
              </th>
              <th className="px-3 py-1.5">
                <ColumnFilter
                  label="Hintaryhmä"
                  options={optionLists.price_group}
                  selected={colFilters.price_group}
                  onChange={updateColFilter('price_group')}
                  testId="customers-filter-pricegroup"
                  labelMap={priceGroupLabels}
                />
              </th>
              <th className="px-3 py-1.5"></th>
              <th className="px-3 py-1.5">
                <ColumnFilter
                  label="Aktiivinen"
                  options={optionLists.active}
                  selected={colFilters.active}
                  onChange={updateColFilter('active')}
                  testId="customers-filter-active"
                />
              </th>
              <th className="px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`${CUSTOMERS.rowPrefix}${c.id}`} className="border-b border-[#E5E6E3] hover:bg-[#F9F9F6] transition-colors">
                <td className="px-2 py-1">
                  <Input
                    type="text"
                    defaultValue={c.nro || ''}
                    key={`nro-${c.id}-${c.nro || ''}`}
                    data-testid={`customer-nro-input-${c.id}`}
                    onBlur={(e) => updateNro(c, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.currentTarget.blur(); }
                      else if (e.key === 'Escape') {
                        e.currentTarget.value = c.nro || '';
                        e.currentTarget.blur();
                      }
                    }}
                    className="h-7 text-xs font-mono text-[#6A6D66] border-transparent bg-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] px-2 w-20"
                  />
                </td>
                <td className="px-3 py-2 text-[#1C1C1A]">{c.ryhma}</td>
                <td className="px-3 py-2 text-[#1C1C1A]">{c.ketju}</td>
                <td className="px-3 py-2 font-medium text-[#1C1C1A]">{c.asiakas}</td>
                <td className="px-2 py-1">
                  <Select value={c.reitti || ''} onValueChange={(v) => updateRoute(c, v)}>
                    <SelectTrigger
                      data-testid={`customer-reitti-select-${c.id}`}
                      className="h-7 text-xs border-transparent bg-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] w-24 px-2"
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {reference.reitti.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`customer-reitti-${c.id}-opt-${r}`}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1">
                  <Select value={String(c.price_group || '')} onValueChange={(v) => updatePriceGroup(c, v)}>
                    <SelectTrigger
                      data-testid={`customer-pricegroup-select-${c.id}`}
                      className="h-7 text-xs border-transparent bg-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] w-28 px-2"
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {(priceGroups.length ? priceGroups.map((g) => g.key) : ['1', '2', '3']).map((g) => (
                        <SelectItem key={g} value={g} data-testid={`customer-pricegroup-${c.id}-opt-${g}`}>
                          {priceGroupLabels[g] || g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-xs text-[#6A6D66]">
                  {c.show_price ? 'Näkyy' : 'Piilossa'}
                </td>
                <td className="px-3 py-2">
                  <Switch
                    checked={!!c.active}
                    onCheckedChange={() => toggleActive(c)}
                    data-testid={`${CUSTOMERS.activeTogglePrefix}${c.id}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`${CUSTOMERS.editPrefix}${c.id}`}
                      className="w-8 h-8 rounded-full hover:bg-[#F4F6F3] text-[#6A6D66] hover:text-[#1C1C1A] flex items-center justify-center transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => remove(c)}
                      data-testid={`${CUSTOMERS.deletePrefix}${c.id}`}
                      className="w-8 h-8 rounded-full hover:bg-[#FBEAE5] text-[#6A6D66] hover:text-[#C05A3C] flex items-center justify-center transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-sm text-[#6A6D66]">Ei tuloksia</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E5E6E3] shrink-0">
            <DialogTitle className="font-heading">{editing ? 'Muokkaa asiakasta' : 'Lisää asiakas'}</DialogTitle>
            <DialogDescription className="text-sm text-[#6A6D66]">
              Aktiiviset asiakkaat näkyvät automaattisesti jokaisella päivällä. Aliakset auttavat PDF-tunnistuksessa.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="customer-dialog-scroll">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nro</Label>
                <Input
                  value={form.nro}
                  onChange={(e) => setForm({ ...form, nro: e.target.value })}
                  data-testid={CUSTOMERS.dialogNroInput}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reitti</Label>
                <Select value={form.reitti} onValueChange={(v) => setForm({ ...form, reitti: v })}>
                  <SelectTrigger data-testid={CUSTOMERS.dialogReittiSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reference.reitti.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Ryhmä</Label>
                <Select
                  value={form.ryhma}
                  onValueChange={(v) => setForm((prev) => {
                    const next = { ...prev, ryhma: v };
                    // Auto-fill välittäjätunnus when ryhmä is changed AND the
                    // current value is either empty or the previously-derived
                    // value (so a user-supplied override is preserved).
                    const prevDerived = operatorForRyhma(prev.ryhma);
                    const nextDerived = operatorForRyhma(v);
                    if (
                      nextDerived &&
                      (!(prev.einvoice_operator_id || '').trim() || prev.einvoice_operator_id === prevDerived)
                    ) {
                      next.einvoice_operator_id = nextDerived;
                    }
                    return next;
                  })}
                >
                  <SelectTrigger data-testid={CUSTOMERS.dialogRyhmaSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reference.ryhma.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ketju</Label>
                <Select value={form.ketju} onValueChange={(v) => setForm({ ...form, ketju: v })}>
                  <SelectTrigger data-testid={CUSTOMERS.dialogKetjuSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reference.ketju.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Asiakas (myymälä)</Label>
              <Input
                value={form.asiakas}
                onChange={(e) => setForm({ ...form, asiakas: e.target.value })}
                data-testid={CUSTOMERS.dialogAsiakasInput}
                placeholder="Esim. Kaari"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Aliakset (pilkulla erotettuna)</Label>
              <Input
                value={Array.isArray(form.aliases) ? form.aliases.join(', ') : form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                data-testid={CUSTOMERS.dialogAliasesInput}
                placeholder="Esim. Prisma Kaari Kannelmäki"
              />
              <p className="text-xs text-[#6A6D66]">PDF-tunnistusta varten — vaihtoehtoiset nimimuodot.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Hintaryhmä</Label>
                <Select value={form.price_group} onValueChange={(v) => setForm({ ...form, price_group: v })}>
                  <SelectTrigger data-testid={CUSTOMERS.dialogPriceGroupSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(priceGroups.length ? priceGroups : [
                      { key: '1', name: 'Kaupat' }, { key: '2', name: 'Tukku' }, { key: '3', name: 'Muu' },
                    ]).map((g) => (
                      <SelectItem key={g.key} value={g.key}>{g.key} {g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Näytä hinta lähetteessä</Label>
                <div className="h-10 flex items-center gap-2 px-1">
                  <Switch
                    checked={!!form.show_price}
                    onCheckedChange={(v) => setForm({ ...form, show_price: v })}
                    data-testid={CUSTOMERS.dialogShowPriceSwitch}
                  />
                  <span className="text-xs text-[#6A6D66]">{form.show_price ? 'Hinta näkyy' : 'Hinta piilotettu'}</span>
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-[#E5E6E3]">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[#6A6D66] font-semibold mb-2">Toimitusohje</p>
              <div className="space-y-1">
                <Label className="text-xs">Lisätietoja toimitukseen</Label>
                <textarea
                  value={form.delivery_note || ''}
                  onChange={(e) => setForm({ ...form, delivery_note: e.target.value })}
                  placeholder="Esim. Ovikoodi 1234 · Tavarat takaovesta · Soita ennen tuloa 040 123 4567"
                  data-testid="customer-delivery-note-input"
                  rows={2}
                  className="w-full text-sm bg-white border border-[#E5E6E3] rounded px-3 py-2 focus:outline-none focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] resize-none"
                />
                <p className="text-[10px] text-[#6A6D66]">Näytetään AINA lähete-PDF:n alaosassa kun tämän asiakkaan lähetteitä tulostetaan. Yksittäistä lähetettä koskeva ad-hoc-huomio voidaan lisätä erikseen Tilauskirjasta rivin tarralapulta.</p>
              </div>
            </div>
            <div className="pt-2 border-t border-[#E5E6E3]">
              <p className="text-[10px] uppercase tracking-[0.12em] text-[#6A6D66] font-semibold mb-2">Laskutus (valinnainen)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Yrityksen nimi (laskutus)</Label>
                  <Input
                    value={form.billing_name || ''}
                    onChange={(e) => setForm({ ...form, billing_name: e.target.value })}
                    placeholder="esim. HOK-Elanto Liiketoiminta Oy"
                    data-testid="customer-billing-name-input"
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Laskutusnimi (Suonentieto)</Label>
                  <Input
                    value={form.lasku_nimi || ''}
                    onChange={(e) => setForm({ ...form, lasku_nimi: e.target.value })}
                    placeholder="esim. Prisma Hyvinkää 222951 — täytä jos eroaa kauppanimestä Suonentiedossa"
                    data-testid="customer-lasku-nimi-input"
                  />
                  <p className="text-[10px] text-[#6A6D66]">Käytetään Finvoice-XML:n vastaanottajakentässä. Jos jätetään tyhjäksi, käytetään yllä olevaa yritysnimeä tai kauppanimeä. Tämä estää Suonentietoa luomasta uutta asiakasta jos nimi eroaa.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Y-tunnus</Label>
                  <Input
                    value={form.ytunnus || ''}
                    onChange={(e) => {
                      const newY = e.target.value;
                      setForm((prev) => {
                        const next = { ...prev, ytunnus: newY };
                        // Auto-fill OVT-tunnus when Y is entered AND the current
                        // OVT is empty or matches the previously-derived value.
                        const prevDerived = ytunnusToOvt(prev.ytunnus);
                        const nextDerived = ytunnusToOvt(newY);
                        if (
                          nextDerived &&
                          (!(prev.einvoice_address || '').trim() || prev.einvoice_address === prevDerived)
                        ) {
                          next.einvoice_address = nextDerived;
                        }
                        return next;
                      });
                    }}
                    placeholder="1234567-8"
                    data-testid="customer-ytunnus-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Katuosoite</Label>
                  <Input
                    value={form.billing_street || ''}
                    onChange={(e) => setForm({ ...form, billing_street: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Postinro</Label>
                  <Input
                    value={form.billing_postal_code || ''}
                    onChange={(e) => setForm({ ...form, billing_postal_code: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Postitoimipaikka</Label>
                  <Input
                    value={form.billing_city || ''}
                    onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                  />
                </div>
              </div>
              <div className="border-t border-[#E5E6E3] pt-3 mt-3 space-y-2">
                <div className="flex items-baseline justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6A6D66]">Verkkolaskutus (Finvoice-reititys)</div>
                  <div className="text-[10px] text-[#6A6D66]" data-testid="customer-einvoice-autofill-hint">
                    Auto-täyttö Y-tunnuksesta ja ryhmästä — voit silti yliajaa kentät käsin.
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">OVT-tunnus / Verkkolaskuosoite</Label>
                    <Input
                      value={form.einvoice_address || ''}
                      onChange={(e) => setForm({ ...form, einvoice_address: e.target.value })}
                      placeholder="esim. 003718379573100"
                      data-testid="customer-einvoice-address-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Välittäjätunnus (operaattori)</Label>
                    <Input
                      value={form.einvoice_operator_id || ''}
                      onChange={(e) => setForm({ ...form, einvoice_operator_id: e.target.value })}
                      placeholder="esim. TietoEvry Oyj (003701011385) / OpusCapita Solutions Oy (E204503) / Opentext Oy (003708599126)"
                      data-testid="customer-einvoice-operator-input"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-[#6A6D66] -mt-1">
                  Suonentieto reitittää laskut <strong>asiakasnumeron (Nro)</strong> perusteella —
                  Nro = Suonentieto-asiakasnumero.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm">Aktiivinen</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-sm">Näkyy kylmiöllä</Label>
                <p className="text-[10px] text-[#6A6D66]">Asiakas näytetään Kylmiö-sivun listalla</p>
              </div>
              <Switch
                checked={!!form.on_kylmio}
                onCheckedChange={(v) => setForm({ ...form, on_kylmio: v })}
                data-testid="customer-on-kylmio"
              />
            </div>
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#E5E6E3] shrink-0 bg-white">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Peruuta</Button>
            <Button
              onClick={save}
              data-testid={CUSTOMERS.saveButton}
              className="bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            >
              Tallenna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />

      <Dialog open={dupModalOpen} onOpenChange={setDupModalOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mahdolliset duplikaattiasiakkaat</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#6A6D66] -mt-2 mb-2">
            Klikkaa <strong>Poista</strong> sen rivin kohdalla, jonka haluat siivota. Vain duplikaatin uudemman/turhan kappaleen poistaminen on yleensä oikein — säilytä se, jolla on täydellisin tieto (lasku_nimi, Y-tunnus, OVT).
          </p>
          <div className="max-h-[60vh] overflow-y-auto space-y-3">
            {dupGroups.length === 0 && (
              <div className="text-sm text-[#6A6D66] text-center py-6">Ei duplikaatteja löytynyt 🎉</div>
            )}
            {dupGroups.map((g, gi) => (
              <div key={gi} className="border border-[#E5E6E3] rounded-lg overflow-hidden" data-testid={`dup-group-${gi}`}>
                <div className="bg-[#F4F6F3] px-3 py-1.5 text-xs font-semibold text-[#6A6D66]">
                  {g.label}
                </div>
                <div className="divide-y divide-[#E5E6E3]">
                  {g.items.map((c) => (
                    <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1C1C1A] truncate">
                          {c.ketju} {c.asiakas}
                          {c.lasku_nimi && (
                            <span className="ml-2 text-[11px] text-[#6A6D66]">· lasku_nimi: {c.lasku_nimi}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#6A6D66]">
                          nro={c.nro || '—'} · Y={c.ytunnus || '—'} · OVT={c.einvoice_address || '—'} · reitti={c.reitti || '—'}
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          if (!confirm(`Poistetaanko asiakas '${c.ketju} ${c.asiakas}' (nro ${c.nro || '—'})?\n\nKaikki sen tilaukset poistetaan.`)) return;
                          try {
                            await api.delete(`/customers/${c.id}`);
                            toast.success(`Poistettu: ${c.asiakas}`);
                            load();
                          } catch {
                            toast.error('Poisto epäonnistui');
                          }
                        }}
                        data-testid={`dup-delete-${c.id}`}
                        className="h-7 rounded-full border-[#C05A3C] text-[#C05A3C] hover:bg-[#FBE9E2]"
                      >
                        <Trash2 size={12} className="mr-1" />
                        Poista
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDupModalOpen(false)}>Sulje</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SortableTh({ field, sort, onClick, className = '', children }) {
  const active = sort.key === field;
  const Icon = active ? (sort.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className={`px-3 py-3 text-xs font-semibold uppercase tracking-wider ${className}`}>
      <button
        type="button"
        onClick={() => onClick(field)}
        data-testid={`customers-sort-${field}`}
        className={`group inline-flex items-center gap-1 transition-colors ${active ? 'text-[#1C1C1A]' : 'text-[#6A6D66] hover:text-[#1C1C1A]'}`}
        title={`Järjestä ${active ? (sort.dir === 'asc' ? 'laskevasti' : 'oletukseen') : 'nousevasti'}`}
      >
        {children}
        <Icon size={12} className={active ? 'opacity-100' : 'opacity-40 group-hover:opacity-80'} />
      </button>
    </th>
  );
}
